<script lang="ts">
  import { assistant } from "./lib/ai/assistant.svelte.ts";
  import { untrack } from "svelte";
  import { onMount } from "svelte";
  import { app } from "./lib/store.svelte.ts";
  import { router, link, groupPath, type GroupPage } from "./lib/router.svelte.ts";
  import { ICONS } from "./lib/icons.ts";
  import SyncBadge from "./components/SyncBadge.svelte";
  import Toasts from "./components/Toasts.svelte";
  import AsOfBanner from "./components/AsOfBanner.svelte";
  import Drawer from "./components/Drawer.svelte";
  import AccountMenu from "./components/AccountMenu.svelte";
  import { privacy } from "./lib/privacy.svelte.ts";
  import { versionWatch } from "./lib/version.svelte.ts";
  import { member } from "./lib/member.svelte.ts";
  import SignIn from "./views/SignIn.svelte";
  import Ledger from "./views/Ledger.svelte";
  import People from "./views/People.svelte";
  import Trips from "./views/Trips.svelte";
  import Insights from "./views/Insights.svelte";
  import History from "./views/History.svelte";
  import GroupSettings from "./views/GroupSettings.svelte";
  import You from "./views/You.svelte";
  import Overview from "./views/Overview.svelte";
  import Accounts from "./views/Accounts.svelte";
  import Roadmap from "./views/Roadmap.svelte";
  import Money from "./views/Money.svelte";
  import Tutoring from "./views/Tutoring.svelte";
  import More from "./views/More.svelte";
  import Invite from "./views/Invite.svelte";
  import PasskeyLink from "./views/PasskeyLink.svelte";
  import { nav } from "./lib/nav.svelte.ts";
  import Welcome from "./views/Welcome.svelte";
  import Ask from "./views/Ask.svelte";
  import Everyone from "./views/Everyone.svelte";
  import Setup from "./views/Setup.svelte";
  import { catalogLookup } from "./lib/ai/lookup.svelte.ts";
  import { aiQueue } from "./lib/ai/queue.svelte.ts";

  const R = $derived(router.route);
  const isPersonView = $derived(R.name === "person-view");

  onMount(() => {
    void app.start();
  });

  // Background AI work goes through one queue: it runs when you're idle (or overnight) and
  // steps aside whenever you ask the AI something.
  aiQueue.start();
  aiQueue.register({
    id: "catalog",
    label: "Products",
    due: () =>
      app.ready && nav.isOwner && !app.memberSession && !!app.groupId && catalogLookup.auto && !catalogLookup.running && catalogLookup.pending().length > 0,
    run: (signal) => catalogLookup.start(false, signal),
    progress: () => (catalogLookup.total ? [catalogLookup.done, catalogLookup.total] : null),
  });

  // Keep the open group in step with the address.
  // Moving to another page shows what's new: a member's group is loaded again (a friend may
  // have ticked meanwhile), and your own changes are synced.
  let lastPath = "";
  $effect(() => {
    const path = JSON.stringify(router.route);
    untrack(() => {
      if (!app.ready || path === lastPath) return;
      const first = lastPath === "";
      lastPath = path;
      if (first) return;
      // Only the group still on screen (opening another group loads that one by itself).
      const r = router.route;
      const gid = r.name === "group" ? r.groupId : null;
      if (!gid || gid === app.memberSession?.groupId) app.refreshMemberGroup();
      app.syncNow();
    });
  });

  $effect(() => {
    if (!app.ready || nav.asMember || R.name !== "group") return;
    if (R.groupId === "") return; // a readable name not matched yet (see the group list effect)
    // An old /g/… link: show the readable one instead, same page.
    if (R.groupId && location.pathname.startsWith("/g/") && groupPath(R.groupId, R.page).startsWith("/group/"))
      router.replace(groupPath(R.groupId, R.page) + location.search);
    if (R.groupId === null) {
      if (app.groupId) router.replace(groupPath(app.groupId, R.page));
    } else if (R.groupId !== app.groupId) {
      if (app.groups.some((g) => g.id === R.groupId)) void app.openGroup(R.groupId);
      else if (app.groups.length) router.replace("/");
    }
  });

  // Everyone else's view (a member, a club treasurer, or you in Member view): their own groups,
  // each opened through the server's cut of it.
  $effect.pre(() => {
    if (nav.asMember) member.setMode(nav.preview ? "preview" : "member");
  });
  $effect(() => {
    if (!app.ready || !nav.asMember) return;
    void member.mode;
    void member.refreshGroups();
    void nav.checkTutoring();
    // Balances stay current too, not just the open group.
    const again = () => document.visibilityState === "visible" && void member.refreshGroups();
    const t = setInterval(again, 30_000);
    document.addEventListener("visibilitychange", again);
    return () => {
      clearInterval(t);
      document.removeEventListener("visibilitychange", again);
    };
  });
  const memberGid = $derived(nav.asMember && R.name === "group" ? R.groupId : null);
  $effect(() => {
    if (!app.ready || !nav.asMember) return;
    const gid = memberGid;
    if (gid && nav.clubTreasurer) {
      untrack(() => {
        app.closeMemberGroup();
        if (app.groupId !== gid) void app.openGroup(gid);
      });
    } else if (gid) {
      const as = nav.preview ? app.previewAs : null;
      untrack(() => void app.openMemberGroup(gid, nav.preview ? "preview" : "member", as));
    } else untrack(() => app.closeMemberGroup());
  });
  $effect(() => {
    if (!nav.asMember) return;
    // Old single-group paths, and pages that are only yours, land on the Overview.
    if (R.name === "group" && R.groupId === null) router.replace("/");
    if (R.name === "money" || R.name === "admin") router.replace("/");
  });
  const memberSession = $derived(app.memberSession?.groupId === memberGid ? app.memberSession : null);

  // Each group's pages. Its Overview is the same dashboard as the main one, for this group only
  // (the page was called Insights; its key and old links stay "insights").
  const groupPages: { page: GroupPage; label: string; icon: string }[] = [
    { page: "insights", label: "Overview", icon: ICONS.overview },
    { page: "ledger", label: "Ledger", icon: ICONS.ledger },
    { page: "people", label: "People", icon: ICONS.people },
    { page: "trips", label: "Receipts", icon: ICONS.receipts },
    { page: "history", label: "History", icon: ICONS.history },
    { page: "settings", label: "Group settings", icon: ICONS.settings },
  ];
  const tabs: { page: GroupPage; label: string; icon: string }[] = [
    { page: "insights", label: "Overview", icon: ICONS.overview },
    { page: "ledger", label: "Ledger", icon: ICONS.ledger },
    { page: "people", label: "People", icon: ICONS.people },
    { page: "trips", label: "Receipts", icon: ICONS.receipts },
    { page: "more", label: "More", icon: ICONS.more },
  ];

  const groupName = (id: string | null) => nav.groups.find((g) => g.id === id)?.name ?? (id && id === nav.openGid ? app.current.name : "");
  const inGroup = $derived(R.name === "group" && R.groupId !== null && R.groupId === nav.openGid);
  /** The page shown: one this account can't open shows the Ledger instead. */
  const currentPage = $derived(R.name === "group" ? (nav.canSee(R.page) ? R.page : "ledger") : null);
  const sidePages = $derived(groupPages.filter((p) => nav.canSee(p.page)));
  const barTabs = $derived(tabs.filter((t) => nav.canSee(t.page)));
  const title = $derived(
    R.name === "overview" ? "Overview" : R.name === "people" ? "People" : R.name === "setup" ? "Set yourself up" : R.name === "you" ? "Your settings" : R.name === "admin" ? "Accounts" : R.name === "roadmap" ? "Roadmap" : R.name === "money" ? "Money" : R.name === "tutoring" ? "Tutoring" : groupName(R.name === "group" ? R.groupId : app.groupId) || "Split Ledger",
  );
  const here = (page: GroupPage) => (inGroup && currentPage === page ? "page" : undefined);

  let switcher = $state(false);
  function go(path: string) {
    switcher = false;
    router.go(path);
  }

  // ---- where you are: the main pages (Overview, People, yours), a group, Tutoring or Money
  type Space = "splits" | "tutoring" | "money" | "you";
  // Ask is one assistant for everything: it stays in whichever space you opened it from.
  let askFrom = $state<Space>("splits");
  const isAsk = $derived(R.name === "group" && R.page === "ask");
  const space = $derived<Space>(
    isAsk ? askFrom : R.name === "money" ? "money" : R.name === "tutoring" ? "tutoring" : R.name === "admin" || R.name === "roadmap" || R.name === "you" || R.name === "people" || R.name === "setup" || R.name === "overview" ? "you" : "splits",
  );
  $effect(() => {
    if (!isAsk) askFrom = R.name === "money" ? "money" : R.name === "tutoring" ? "tutoring" : R.name === "group" ? "splits" : "you";
    assistant.defaultScope = askFrom === "money" ? "money" : askFrom === "tutoring" ? "tutoring" : askFrom === "you" ? "everything" : "group";
  });
  const askPath = $derived(app.groupId ? groupPath(app.groupId, "ask") : app.groups[0] ? groupPath(app.groups[0].id, "ask") : "/ask");
  const showAsk = $derived(nav.isOwner);

  /** Tutoring's pages: yours are Calendar, Students and Tutors; a family or tutor has their own one. */
  const tutoringTabs = $derived<(readonly [string, string, string])[]>(
    nav.isOwner
      ? [
          ["calendar", "Calendar", ICONS.history],
          ["students", "Students", ICONS.people],
          ["tutors", "Tutors", ICONS.tutoring],
        ]
      : [["students", "Tutoring", ICONS.tutoring]],
  );
  /** The phone's bottom bar follows the space you're in, with "Spaces" to switch. */
  const bar = $derived.by(() => {
    type Tab = { href: string; label: string; icon: string; current: boolean };
    const out: Tab[] = [];
    if (barSpace === "tutoring") {
      for (const [t, label, icon] of tutoringTabs)
        out.push({ href: `/tutoring/${t}`, label, icon, current: R.name === "tutoring" && (R.tab === t || tutoringTabs.length === 1) });
    } else if (barSpace === "money") {
      out.push({ href: "/money", label: "Money", icon: ICONS.money, current: R.name === "money" });
    } else if (barSpace === "splits" && nav.openGid && R.name === "group") {
      for (const t of barTabs)
        out.push({
          href: groupPath(nav.openGid, t.page),
          label: t.label,
          icon: t.icon,
          current:
            !!here(t.page) || (t.page === "more" && inGroup && ["history", "settings"].includes(currentPage ?? "")),
        });
    } else {
      out.push({ href: "/", label: "Overview", icon: ICONS.overview, current: R.name === "overview" });
      out.push({ href: "/everyone", label: "People", icon: ICONS.people, current: R.name === "people" });
    }
    return out;
  });
  /** The phone's bottom bar: the main pages, the open group's, Tutoring's or Money. */
  const barSpace = $derived(isAsk ? askFrom : space);
  const barLabel = $derived(barSpace === "tutoring" ? "Tutoring" : barSpace === "money" ? "Money" : barSpace === "splits" && R.name === "group" ? "Group" : "Main");
  /** What Ask will be about, from where you are. */
  const askAbout = $derived(askFrom === "money" ? "Money" : askFrom === "tutoring" ? "Tutoring" : askFrom === "splits" ? groupName(app.groupId) || "this group" : "everything");

  // Privacy mode hides people's names too: keep its list current.
  privacy.start();
  versionWatch.start();
  versionWatch.autoApply(() => app.pendingCount === 0);
  // Readable group links (/group/costco-club): keep their names in step with the group list.
  $effect(() => {
    router.setGroups([
      ...app.groups.map((g) => ({ id: g.id, name: g.name })),
      ...(member.groups ?? []).map((g) => ({ id: g.id, name: g.name })),
    ]);
    // A readable name that matches none of your groups (renamed, or not yours): go home.
    untrack(() => {
      const r = router.route;
      if (r.name === "group" && r.groupId === "" && (app.groups.length || member.groups?.length)) router.replace("/");
    });
  });
  $effect(() => {
    // The open group (yours, or the one a member is looking at) has everyone's names.
    privacy.setNames(Object.values(app.current?.people ?? {}).map((p) => p.name));
  });
</script>

{#if isPersonView && R.name === "person-view"}
  <Invite token={R.token} />
{:else if R.name === "passkey-link"}
  <PasskeyLink token={R.token} />
{:else if !app.ready}
  <div class="boot" aria-busy="true"></div>
{:else if app.signedIn === false && app.events.length === 0 && app.groups.length === 0}
  <SignIn />
{:else if !nav.asMember && !app.groupId && R.name !== "you" && R.name !== "people" && R.name !== "setup" && R.name !== "admin" && R.name !== "roadmap" && R.name !== "money" && R.name !== "tutoring"}
  <Welcome />
{:else}
  <div class="shell">
    <aside class="side">
      <div class="brand">
        <svg viewBox="0 0 64 64" width="28" height="28" aria-hidden="true">
          <rect width="64" height="64" rx="14" fill="currentColor" />
          <circle cx="24" cy="32" r="11" fill="#e5484d" />
          <path d="M40 21a11 11 0 0 1 0 22z" fill="#3b7ddd" />
          <circle cx="40" cy="32" r="10" fill="none" stroke="#3b7ddd" stroke-width="2" />
        </svg>
        <div>
          <div class="brand-name">Split Ledger</div>
          <SyncBadge />
        </div>
      </div>

      <nav class="side-nav" aria-label="Main">
        <a href="/" use:link class="nav-link" aria-current={R.name === "overview" ? "page" : undefined}>
          <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true"><path d={ICONS.overview} /></svg>Overview
        </a>
        <a href="/everyone" use:link class="nav-link" aria-current={R.name === "people" ? "page" : undefined}>
          <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true"><path d={ICONS.people} /></svg>People
        </a>
        <div class="section-label" class:hidden={!nav.isOwner && !nav.groups.length}>
          <span>Groups</span>
          {#if nav.isOwner}
            <a href="/you#groups" use:link class="add" aria-label="New group" title="New group">
              <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true"><path d={ICONS.plus} /></svg>
            </a>
          {/if}
        </div>
        {#each nav.groups as g (g.id)}
          {@const open = nav.asMember ? R.name === "group" && g.id === R.groupId : g.id === app.groupId && (R.name === "group")}
          <a href={groupPath(g.id)} use:link class="group-link" class:open>
            <span class="gdot" aria-hidden="true"></span>
            <span class="gname">{g.name}</span>
            {#if !open}<svg class="chev" viewBox="0 0 24 24" width="16" height="16" aria-hidden="true"><path d={ICONS.chevron} /></svg>{/if}
          </a>
          {#if open}
            <div class="sub" aria-label="{g.name} pages" role="group">
              {#each sidePages as p}
                <a href={groupPath(g.id, p.page)} use:link class="nav-link" aria-current={here(p.page)}>
                  <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true"><path d={p.icon} /></svg>{p.label}
                </a>
              {/each}
            </div>
          {/if}
        {/each}
        {#if nav.spaces.includes("tutoring")}
          <div class="section-label"><span>Tutoring</span></div>
          <div class="sub" aria-label="Tutoring pages" role="group">
            {#each tutoringTabs as [t, label, icon] (t)}
              <a href="/tutoring/{t}" use:link class="nav-link" aria-current={R.name === "tutoring" && (R.tab === t || tutoringTabs.length === 1) ? "page" : undefined}>
                <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true"><path d={icon} /></svg>{label}
              </a>
            {/each}
          </div>
        {/if}
        {#if nav.spaces.includes("money")}
          <div class="section-label"><span>Money</span></div>
          <a href="/money" use:link class="nav-link" aria-current={R.name === "money" ? "page" : undefined}>
            <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true"><path d={ICONS.money} /></svg>Money
          </a>
        {/if}
      </nav>

      <nav class="you-nav" aria-label="You">
        {#if nav.isOwner}
          <a href="/admin" use:link class="nav-link small" aria-current={R.name === "admin" ? "page" : undefined}>
            <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true"><path d={ICONS.accounts} /></svg>Accounts
          </a>
        {/if}
        <a href="/roadmap" use:link class="nav-link small" aria-current={R.name === "roadmap" ? "page" : undefined}>
          <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true"><path d={ICONS.roadmap} /></svg>Roadmap
        </a>
        <a href="/you" use:link class="nav-link small" aria-current={R.name === "you" ? "page" : undefined}>
          <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true"><path d={ICONS.you} /></svg>Your settings
        </a>
      </nav>
      <div class="account"><AccountMenu /></div>
    </aside>

    <div class="main">
      <header class="topbar">
        <button class="switch" onclick={() => (switcher = true)} aria-haspopup="dialog">
          <span>{title}</span>
          <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true"><path d={ICONS.chevron} /></svg>
        </button>
        <span class="top-right"><SyncBadge /><AccountMenu variant="avatar" /></span>
      </header>
      <AsOfBanner />
      {#if app.signedIn === false}
        <div class="notice">
          You're signed out. Changes stay on this device until you
          <a href="/you" use:link>sign in again</a>.
        </div>
      {/if}
      {#if nav.preview}
        <div class="viewing" role="status">
          <span><strong>Member view.</strong> {app.previewAs && memberSession ? `Exactly what ${memberSession.state.people[memberSession.personId]?.name ?? "they"} sees.` : "This is what friends' accounts look like, shown for your own items."}</span>
          <button class="btn small" onclick={() => app.setViewAsMember(false)}>Back to treasurer view</button>
        </div>
      {/if}
      <main>
        {#if R.name === "overview"}<Overview />
        {:else if R.name === "people"}<Everyone />
        {:else if R.name === "setup"}<Setup />
        {:else if R.name === "you"}<You />
        {:else if R.name === "admin" && nav.isOwner}<Accounts />
        {:else if R.name === "roadmap"}<Roadmap />
        {:else if R.name === "money" && nav.isOwner}<Money />
        {:else if R.name === "tutoring"}<Tutoring />
        {:else if inGroup && R.name === "group"}
          {#if nav.clubTreasurer}<p class="role-note muted">You're this club's treasurer: you see and change everything in it.</p>{/if}
          {#key `${R.groupId}:${app.memberSession?.personId ?? ""}`}
            {#if currentPage === "ledger"}<Ledger />
            {:else if currentPage === "people" || currentPage === "payments"}<People /><!-- payments live under each person now -->
            {:else if currentPage === "trips"}<Trips />
            {:else if currentPage === "insights"}<Insights />
            {:else if currentPage === "history"}<History />
            {:else if currentPage === "settings"}<GroupSettings />
            {:else if currentPage === "ask"}<Ask />
            {:else if currentPage === "more"}<More />
            {/if}
          {/key}
        {:else if R.name === "group" && nav.asMember}
          {#if app.memberError}<div class="empty">{app.memberError}</div>
          {:else}<p class="muted opening">Opening {groupName(R.groupId) || "the group"}…</p>{/if}
        {/if}
      </main>
    </div>

    {#if showAsk && !isAsk}
      <a class="askbar" href={askPath} use:link aria-label="Ask about {askAbout}">
        <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true"><path d={ICONS.ask} /></svg>
        <span>Ask about {askAbout}</span>
      </a>
    {/if}

    <nav class="tabbar" aria-label={barLabel}>
      {#each bar as t (t.label)}
        <a href={t.href} use:link aria-current={t.current ? "page" : undefined}>
          <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true"><path d={t.icon} /></svg>
          <span>{t.label}</span>
        </a>
      {/each}
      <button class="tab-btn" onclick={() => (switcher = true)} aria-haspopup="dialog">
        <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true"><path d={ICONS[barSpace === "tutoring" ? "tutoring" : barSpace === "money" ? "money" : "splits"]} /></svg>
        <span>Spaces</span>
      </button>
    </nav>
  </div>

  {#if switcher}
    <Drawer title="Go to" onclose={() => (switcher = false)}>
      <nav class="switch-list" aria-label="Go to">
        <button class:on={R.name === "overview"} onclick={() => go("/")}>
          <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true"><path d={ICONS.overview} /></svg>Overview
        </button>
        <button class:on={R.name === "people"} onclick={() => go("/everyone")}>
          <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true"><path d={ICONS.people} /></svg>People
        </button>
        <p class="switch-label">Groups</p>
        {#each nav.groups as g (g.id)}
          <button class:on={inGroup && g.id === nav.openGid} onclick={() => go(groupPath(g.id, currentPage && g.id !== nav.openGid ? "ledger" : (currentPage ?? "ledger")))}>
            <span class="gdot" aria-hidden="true"></span>{g.name}
          </button>
        {/each}
        {#if nav.spaces.includes("tutoring")}
          <p class="switch-label">Tutoring</p>
          {#each tutoringTabs as [t, label, icon] (t)}
            <button class:on={R.name === "tutoring" && (R.tab === t || tutoringTabs.length === 1)} onclick={() => go(`/tutoring/${t}`)}>
              <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true"><path d={icon} /></svg>{label}
            </button>
          {/each}
        {/if}
        {#if nav.spaces.includes("money")}
          <p class="switch-label">Money</p>
          <button class:on={R.name === "money"} onclick={() => go("/money")}>
            <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true"><path d={ICONS.money} /></svg>Money
          </button>
        {/if}
        <p class="switch-label">You</p>
        {#if nav.isOwner}
          <button class:on={R.name === "admin"} onclick={() => go("/admin")}>
            <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true"><path d={ICONS.accounts} /></svg>Accounts
          </button>
        {/if}
        <button class:on={R.name === "roadmap"} onclick={() => go("/roadmap")}>
          <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true"><path d={ICONS.roadmap} /></svg>Roadmap
        </button>
        <button class:on={R.name === "you"} onclick={() => go("/you")}>
          <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true"><path d={ICONS.you} /></svg>Your settings
        </button>
      </nav>
    </Drawer>
  {/if}
{/if}
{#if versionWatch.available}
  <div class="update-bar" role="status">
    <span class="msg">Update ready</span>
    <button class="go" onclick={() => location.reload()}>Update now</button>
    <button class="x" aria-label="Dismiss" onclick={() => (versionWatch.available = null)}>×</button>
  </div>
{/if}
<Toasts />

<style>
  .update-bar {
    position: fixed;
    top: calc(env(safe-area-inset-top) + 8px);
    left: 50%;
    transform: translateX(-50%);
    z-index: 60;
    display: flex;
    align-items: center;
    gap: 8px;
    max-width: calc(100vw - 32px);
    padding: 6px 6px 6px 14px;
    border-radius: 12px;
    background: var(--ink);
    color: var(--paper);
    box-shadow: var(--shadow-pop);
    font-size: var(--t-sm);
    white-space: nowrap;
  }

  .update-bar .msg {
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .update-bar button {
    flex: none;
    border: 0;
    border-radius: 8px;
    padding: 5px 10px;
    font: inherit;
    font-weight: 650;
    cursor: pointer;
  }

  .update-bar .go {
    background: var(--paper);
    color: var(--ink);
  }

  .update-bar .x {
    background: none;
    color: var(--paper);
    font-size: 1.1rem;
    line-height: 1;
    padding: 4px 8px;
  }

  .boot {
    min-height: 100vh;
  }

  .shell {
    min-height: 100vh;
    display: grid;
    grid-template-columns: 232px 1fr;
  }

  .side {
    position: sticky;
    top: 0;
    height: 100vh;
    padding: 20px 14px;
    border-right: 1px solid var(--rule);
    display: flex;
    flex-direction: column;
    gap: 24px;
  }

  .brand {
    display: flex;
    gap: 10px;
    align-items: flex-start;
    color: var(--ink);
    padding: 0 6px;
  }

  .brand-name {
    font-weight: 700;
    font-size: var(--t-md);
    line-height: 1.2;
  }

  .side-nav {
    display: grid;
    align-content: start;
    gap: 2px;
    flex: 1;
    overflow-y: auto;
  }

  .you-nav {
    display: grid;
    gap: 0;
    padding: 8px 0;
    border-top: 1px solid var(--rule);
  }

  .you-nav .nav-link.small {
    padding-top: 6px;
    padding-bottom: 6px;
    font-size: var(--t-sm);
  }

  .account {
    padding-top: 10px;
    border-top: 1px solid var(--rule);
  }

  .top-right {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .section-label {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin: 18px 6px 6px 10px;
    font-size: var(--t-xs);
    font-weight: 650;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: var(--muted);
  }

  .add {
    display: grid;
    place-items: center;
    width: 26px;
    height: 26px;
    border-radius: 8px;
    color: var(--muted);
  }

  .add:hover {
    background: var(--wash);
    color: var(--ink);
  }

  .group-link {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 9px 10px;
    border-radius: var(--r-md);
    text-decoration: none;
    color: var(--ink-2);
    font-weight: 600;
  }

  .group-link:hover {
    background: var(--wash);
  }

  .group-link.open {
    color: var(--ink);
  }

  .gname {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .gdot {
    flex: none;
    width: 10px;
    height: 10px;
    margin: 0 5px;
    border-radius: 50%;
    background: var(--muted);
  }

  .group-link.open .gdot {
    background: #3b7ddd;
  }

  .chev {
    color: var(--muted);
    transform: rotate(-90deg);
  }

  .sub {
    display: grid;
    gap: 1px;
    margin: 0 0 6px 15px;
    padding-left: 10px;
    border-left: 1px solid var(--rule);
  }

  .sub .nav-link {
    padding: 7px 10px;
    font-size: var(--t-sm);
  }

  .switch {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    min-width: 0;
    padding: 6px 8px 6px 0;
    border: 0;
    background: none;
    color: var(--ink);
    font: inherit;
    font-weight: 700;
    font-size: var(--t-lg);
  }

  .switch span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .switch-list {
    display: grid;
    gap: 2px;
  }

  .switch-list button {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px 10px;
    border: 0;
    border-radius: var(--r-md);
    background: none;
    color: var(--ink);
    font: inherit;
    font-weight: 550;
    text-align: left;
  }

  .switch-list button:hover,
  .switch-list button.on {
    background: var(--wash);
  }

  .switch-label {
    margin: 14px 10px 4px;
    font-size: var(--t-xs);
    font-weight: 650;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: var(--muted);
  }

  .nav-link {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 9px 10px;
    border-radius: var(--r-md);
    text-decoration: none;
    color: var(--ink-2);
    font-weight: 550;
  }

  .nav-link:hover {
    background: var(--wash);
  }

  .nav-link[aria-current="page"] {
    background: var(--surface);
    color: var(--ink);
    box-shadow: inset 0 0 0 1px var(--rule);
  }

  svg path {
    fill: none;
    stroke: currentColor;
    stroke-width: 1.7;
    stroke-linecap: round;
    stroke-linejoin: round;
  }

  .main {
    min-width: 0;
  }

  .topbar {
    display: none;
  }

  .section-label.hidden {
    display: none;
  }

  .viewing {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    flex-wrap: wrap;
    margin: 12px var(--gutter) 0;
    padding: 10px 14px;
    border-radius: var(--r-md);
    background: color-mix(in srgb, #8b5cd6 14%, transparent);
    font-size: var(--t-sm);
  }

  .role-note {
    margin: 12px var(--gutter) 0;
    font-size: var(--t-xs);
  }

  .opening,
  .empty {
    margin: 24px var(--gutter);
  }

  .notice {
    margin: 12px var(--gutter) 0;
    padding: 10px 14px;
    border-radius: var(--r-md);
    background: var(--wash);
    font-size: var(--t-sm);
  }

  .tabbar {
    display: none;
  }

  /* Ask: one bar, everywhere, about wherever you are (not a tab in each space). */
  .askbar {
    position: fixed;
    right: 20px;
    bottom: 20px;
    z-index: 25;
    display: inline-flex;
    align-items: center;
    gap: 8px;
    max-width: min(360px, calc(100vw - 40px));
    padding: 10px 16px;
    border-radius: 999px;
    background: var(--surface);
    color: var(--ink-2);
    box-shadow: var(--shadow-pop), inset 0 0 0 1px var(--rule);
    font-size: var(--t-sm);
    font-weight: 600;
    text-decoration: none;
  }

  .askbar span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .askbar:hover {
    color: var(--ink);
  }

  @media (max-width: 860px) {
    .shell {
      grid-template-columns: 1fr;
    }

    .side {
      display: none;
    }

    .topbar {
      position: sticky;
      top: 0;
      z-index: 20;
      display: flex;
      justify-content: space-between;
      align-items: center;
      gap: 12px;
      padding: calc(env(safe-area-inset-top) + 10px) var(--gutter) 10px;
      background: color-mix(in srgb, var(--paper) 88%, transparent);
      backdrop-filter: blur(12px);
      border-bottom: 1px solid var(--rule);
    }

    .tabbar {
      position: fixed;
      inset: auto 0 0 0;
      z-index: 30;
      display: grid;
      grid-auto-flow: column;
      grid-auto-columns: minmax(0, 1fr);
      padding: 6px 4px calc(env(safe-area-inset-bottom) + 6px);
      background: color-mix(in srgb, var(--surface) 92%, transparent);
      backdrop-filter: blur(12px);
      border-top: 1px solid var(--rule);
    }

    .tabbar a,
    .tabbar .tab-btn {
      display: grid;
      justify-items: center;
      gap: 2px;
      padding: 4px 0;
      border: 0;
      background: none;
      text-decoration: none;
      color: var(--muted);
      font: inherit;
      font-size: 0.6875rem;
      font-weight: 600;
      cursor: pointer;
    }

    .tabbar a[aria-current="page"] {
      color: var(--ink);
    }

    .askbar {
      left: var(--gutter);
      right: var(--gutter);
      bottom: calc(env(safe-area-inset-bottom) + 66px);
      max-width: none;
      justify-content: center;
    }
  }
</style>
